import { useEffect, useMemo, useRef, useState } from 'react'
import { MapContainer, TileLayer, Marker, Circle, Popup } from 'react-leaflet'
import L from 'leaflet'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts'
import Layout from '../components/Layout'
import { auth } from '../firebase'
import {
  subscribeChild,
  setChildAppBlocked,
  updateScreenLock,
  subscribeParentChildren,
  updateWebFilter,
  unlinkChildFromParent,
  updateRoutines,
  subscribeRoutines,
  updateDailyQuota,
  subscribeDailyQuota,
  requestUninstallCode,
  subscribeUninstallRequest,
  clearUnlockRequest,
} from '../lib/db'
import { formatScreenLock } from '../lib/format'
import { appEmoji } from '../lib/appIcons'
import { toast } from '../lib/toast'
import { AGE_BAND_LABELS, WEB_CATEGORY_LABELS, presetFor } from '../lib/webFilter'
import type { Child, Routines, WeekDay } from '../lib/types'
import { WEEK_DAYS } from '../lib/types'
import type { Tab, NavDest } from '../lib/nav'
import type { AgeBand, WebCategory } from '../lib/types'

const customIcon = L.divIcon({
  className: 'bg-transparent',
  html: '<div style="width:28px;height:28px;border-radius:50%;background:#2569f2;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,.3)"></div>',
  iconSize: [28, 28],
  iconAnchor: [14, 14],
})

export default function ChildDetailPage({
  uid,
  tab,
  onBack,
  isAdmin,
  onOpenAdmin,
  onOpenAccount,
  onOpenDownloads,
  onOpenChild,
}: {
  uid: string
  tab?: Tab
  onBack: () => void
  isAdmin?: boolean
  onOpenAdmin?: () => void
  onOpenAccount?: () => void
  onOpenDownloads?: () => void
  onOpenChild?: (uid: string, tab?: Tab) => void
}) {
  const [child, setChild] = useState<Child | null>(null)
  const [activeTab, setActiveTab] = useState<Tab>(tab ?? 'apps')
  const [devices, setDevices] = useState<{ uid: string; data: Child }[]>([])
  const parentUid = auth.currentUser?.uid ?? ''

  useEffect(() => {
    setActiveTab(tab ?? 'apps')
  }, [tab, uid])

  useEffect(() => {
    return subscribeChild(uid, setChild)
  }, [uid])

  useEffect(() => {
    if (!parentUid) return
    return subscribeParentChildren(parentUid, setDevices)
  }, [parentUid])

  const handleNav = (dest: NavDest) => {
    if (dest === 'home') {
      onBack()
    } else if (dest === 'account') {
      onOpenAccount?.()
    } else if (dest === 'downloads') {
      onOpenDownloads?.()
    } else if (dest === 'apps') {
      setActiveTab('apps')
    } else {
      setActiveTab('activity')
    }
  }

  const tabs: { id: Tab; label: string }[] = [
    { id: 'activity', label: 'Actividad' },
    { id: 'apps', label: 'Aplicaciones' },
    { id: 'webfilter', label: 'Filtros web' },
    { id: 'routines', label: 'Rutinas' },
    { id: 'calls', label: 'Llamadas' },
    { id: 'sms', label: 'SMS' },
    { id: 'contacts', label: 'Contactos' },
    { id: 'location', label: 'Ubicación' },
  ]

  return (
    <Layout
      title={child?.name ? `${child.name} — v${child.agentVersion ?? '?'} — dispositivo` : 'Dispositivo'}
      onBack={onBack}
      onNavigate={handleNav}
      isAdmin={isAdmin}
      onAdmin={onOpenAdmin}
      onAccount={onOpenAccount}
      onDownloads={onOpenDownloads}
      action={child ? (
        <div className="flex items-center gap-2">
          <ScreenLockControl childUid={uid} lock={child.screenLock} />
          <button
            onClick={async () => {
              if (!confirm('¿Eliminar este dispositivo? Se desvinculará de tu cuenta y dejará de reportar actividad.')) return
              try {
                await unlinkChildFromParent(parentUid, uid)
                toast('🗑️ Dispositivo eliminado', 'success')
                onBack()
              } catch {
                toast('No se pudo eliminar el dispositivo', 'error')
              }
            }}
            className="rounded-xl px-4 py-2 text-sm font-semibold text-white transition bg-rose-500 hover:bg-rose-600"
            title="Eliminar dispositivo"
          >
            🗑️ Eliminar
          </button>
        </div>
      ) : undefined}
    >
      <div className="mb-4 flex flex-wrap items-center gap-3">
        {devices.length > 1 && (
          <select
            value={uid}
            onChange={(e) => onOpenChild?.(e.target.value, activeTab)}
            className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 outline-none focus:border-brand-500"
          >
            {devices.map((d) => (
              <option key={d.uid} value={d.uid}>
                {d.data.deviceType === 'windows' ? '🖥️' : '🤖'} {d.data.name} (v{d.data.agentVersion ?? '?'})
              </option>
            ))}
          </select>
        )}
        {child && <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-mono text-slate-600">v{child.agentVersion ?? '?'} · {child.deviceType ?? '?'}</span>}
        <div className="flex flex-wrap gap-2">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${
                activeTab === t.id
                  ? 'bg-brand-600 text-white shadow-sm'
                  : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {child?.unlockRequest && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-5 py-4">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🙋</span>
            <div>
              <p className="font-semibold text-amber-800">{child.name} está pidiendo desbloquear el dispositivo</p>
              <p className="text-xs text-amber-600">
                Solicitud recibida{' '}
                {child.unlockRequest.requestedAt
                  ? new Date(child.unlockRequest.requestedAt).toLocaleTimeString()
                  : ''}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => {
                updateScreenLock(uid, { ...(child.screenLock ?? {}), locked: false, lockUntil: null })
                clearUnlockRequest(uid)
                toast('🔓 Dispositivo desbloqueado', 'success')
              }}
              className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
            >
              🔓 Desbloquear
            </button>
            <button
              onClick={() => clearUnlockRequest(uid)}
              className="rounded-xl border border-amber-300 bg-white px-4 py-2 text-sm font-semibold text-amber-700 hover:bg-amber-100"
            >
              Ignorar
            </button>
          </div>
        </div>
      )}

      {!child ? (
        <div className="rounded-2xl bg-white p-10 text-center text-slate-500 ring-1 ring-slate-200">
          Cargando…
        </div>
      ) : (
        <div>
          {activeTab === 'activity' && <ActivityTab childUid={uid} child={child} />}
          {activeTab === 'apps' && <AppsTab childUid={uid} child={child} />}
          {activeTab === 'webfilter' && <WebFilterTab childUid={uid} child={child} />}
          {activeTab === 'routines' && <RoutinesTab childUid={uid} />}
          {activeTab === 'calls' && <CallsTab child={child} />}
          {activeTab === 'sms' && <SmsTab child={child} />}
          {activeTab === 'contacts' && <ContactsTab child={child} />}
          {activeTab === 'location' && <LocationTab child={child} />}
          <div className="mt-8"><UninstallSection childUid={uid} childName={child.name ?? 'dispositivo'} /></div>
          {child.lastCrash && (
            <div className="mt-8 rounded-2xl border border-rose-200 bg-rose-50 p-5">
              <h4 className="font-bold text-rose-800">Diagnóstico · último error del dispositivo</h4>
              <p className="mt-1 text-xs text-rose-700">Copia este texto y envíalo a soporte para revisar el fallo.</p>
              <pre className="mt-3 max-h-64 overflow-auto whitespace-pre-wrap rounded-xl bg-white p-4 text-xs text-slate-700 ring-1 ring-rose-200">{child.lastCrash}</pre>
            </div>
          )}
        </div>
      )}
    </Layout>
  )
}

function ScreenLockControl({
  childUid,
  lock,
}: {
  childUid: string
  lock: Child['screenLock']
}) {
  const locked = Boolean(lock?.locked)
  const [open, setOpen] = useState(false)
  const [now, setNow] = useState(Date.now())
  const [note, setNote] = useState(lock?.note ?? '')
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [])

  const lockUntil = lock?.lockUntil ? Date.parse(lock.lockUntil) : 0
  const remaining = locked && lockUntil > 0 ? Math.max(0, lockUntil - now) : 0
  const fmt = (ms: number) => {
    const total = Math.floor(ms / 1000)
    const h = Math.floor(total / 3600)
    const m = Math.floor((total % 3600) / 60)
    const s = total % 60
    return h > 0
      ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
      : `${m}:${String(s).padStart(2, '0')}`
  }

  const doLock = (minutes: number | null) => {
    const lockUntil = minutes ? new Date(Date.now() + minutes * 60000).toISOString() : null
    updateScreenLock(childUid, { ...(lock ?? {}), locked: true, lockUntil, note: note.trim() || null })
    toast(
      minutes
        ? `🔒 Dispositivo bloqueado por ${minutes} min`
        : '🔒 Dispositivo bloqueado hasta que lo desbloquees',
      'success',
    )
    setOpen(false)
  }

  const doUnlock = () => {
    updateScreenLock(childUid, { ...(lock ?? {}), locked: false, lockUntil: null })
    toast('🔓 Dispositivo desbloqueado', 'success')
    setOpen(false)
  }

  if (locked) {
    return (
      <div className="flex items-center gap-2">
        {remaining > 0 && (
          <span className="rounded-lg bg-slate-100 px-2.5 py-1 font-mono text-xs text-slate-600">
            ⏳ {fmt(remaining)}
          </span>
        )}
        <button
          onClick={doUnlock}
          className="rounded-xl bg-rose-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-rose-600"
        >
          🔒 Desbloquear
        </button>
      </div>
    )
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="rounded-xl bg-emerald-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-600"
        title="Bloquear el dispositivo ahora"
      >
        🔓 Bloquear ahora
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-2 w-64 overflow-hidden rounded-xl bg-white py-1 shadow-xl ring-1 ring-slate-200">
          <div className="px-3 py-2">
            <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              Nota para el hijo (opcional)
            </label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Ej: Es hora de dormir"
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-500"
            />
          </div>
          <p className="border-t border-slate-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            Duración del bloqueo
          </p>
          {[
            { label: '15 minutos', min: 15 },
            { label: '30 minutos', min: 30 },
            { label: '1 hora', min: 60 },
            { label: '2 horas', min: 120 },
          ].map((o) => (
            <button
              key={o.min}
              onClick={() => doLock(o.min)}
              className="block w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100"
            >
              ⏱️ {o.label}
            </button>
          ))}
          <button
            onClick={() => doLock(null)}
            className="block w-full border-t border-slate-100 px-3 py-2 text-left text-sm font-semibold text-rose-600 hover:bg-rose-50"
          >
            🔒 Hasta que yo desbloquee
          </button>
        </div>
      )}
    </div>
  )
}

function WebFilterTab({ childUid, child }: { childUid: string; child: Child }) {
  const [enabled, setEnabled] = useState(child.webFilter?.enabled ?? true)
  const [ageBand, setAgeBand] = useState<AgeBand>(child.webFilter?.ageBand ?? 'under13')
  const [categories, setCategories] = useState<Record<WebCategory, boolean>>(
    child.webFilter?.categories ?? presetFor('under13'),
  )
  const [domains, setDomains] = useState((child.webFilter?.customDomains ?? []).join('\n'))
  const [saving, setSaving] = useState(false)

  // Inicializar SOLO al montar el tab. Si sincronizáramos con cada snapshot de
  // Firebase (llega cada segundo), se pisarían los toggles que el padre aún no
  // ha guardado y el formulario parecería "no guardar".
  const initialFilter = useRef(JSON.stringify(child.webFilter ?? null))
  useEffect(() => {
    const current = JSON.stringify(child.webFilter ?? null)
    // Solo resincronizar si el cambio viene de OTRO dispositivo/sesión y el
    // usuario local no está editando (sin cambios pendientes = states iguales al montaje)
    if (current !== initialFilter.current && !saving) {
      initialFilter.current = current
      setEnabled(child.webFilter?.enabled ?? true)
      setAgeBand(child.webFilter?.ageBand ?? 'under13')
      setCategories(child.webFilter?.categories ?? presetFor('under13'))
      setDomains((child.webFilter?.customDomains ?? []).join('\n'))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [child.webFilter])

  const applyBand = (band: AgeBand) => {
    setAgeBand(band)
    setCategories(presetFor(band))
  }

  const toggleCategory = (cat: WebCategory) => {
    setCategories((prev) => ({ ...prev, [cat]: !prev[cat] }))
  }

  const save = async () => {
    setSaving(true)
    try {
      const customDomains = domains
        .split('\n')
        .map((d) => d.trim().toLowerCase())
        .filter(Boolean)
      await updateWebFilter(childUid, {
        enabled,
        ageBand,
        categories,
        customDomains,
      })
      toast('✅ Filtros web guardados. El agente los aplicará en menos de 30 segundos.', 'success')
    } catch (e: unknown) {
      toast((e as { message?: string }).message ?? 'Error al guardar los filtros', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="max-w-2xl space-y-5">
      <div className="flex items-center justify-between rounded-2xl bg-white p-5 ring-1 ring-slate-200">
        <div>
          <h3 className="font-bold text-slate-800">Filtros Web Inteligentes</h3>
          <p className="text-sm text-slate-500">
            Bloquea páginas por rango de edad en este dispositivo (Windows y Android).
          </p>
        </div>
        <label className="relative inline-flex cursor-pointer items-center">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="peer sr-only"
          />
          <div className="h-7 w-14 rounded-full bg-slate-300 after:absolute after:left-1 after:top-1 after:h-5 after:w-5 after:rounded-full after:bg-white after:transition peer-checked:bg-brand-600 peer-checked:after:translate-x-7" />
        </label>
      </div>

      <div className="rounded-2xl bg-white p-5 ring-1 ring-slate-200">
        <h3 className="font-bold text-slate-800">Rango de edad</h3>
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          {(Object.keys(AGE_BAND_LABELS) as AgeBand[]).map((band) => (
            <button
              key={band}
              onClick={() => applyBand(band)}
              className={`rounded-xl border-2 p-4 text-left transition ${
                ageBand === band
                  ? 'border-brand-600 bg-brand-50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <p className="font-semibold text-slate-800">{AGE_BAND_LABELS[band].label}</p>
              <p className="mt-1 text-xs text-slate-500">{AGE_BAND_LABELS[band].desc}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-2xl bg-white p-5 ring-1 ring-slate-200">
        <h3 className="font-bold text-slate-800">Categorías a bloquear</h3>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {(Object.keys(WEB_CATEGORY_LABELS) as WebCategory[]).map((cat) => (
            <label
              key={cat}
              className="flex cursor-pointer items-center justify-between rounded-xl border border-slate-200 px-4 py-3 hover:bg-slate-50"
            >
              <span className="text-sm font-medium text-slate-700">
                {WEB_CATEGORY_LABELS[cat].emoji} {WEB_CATEGORY_LABELS[cat].label}
              </span>
              <input
                type="checkbox"
                checked={Boolean(categories[cat])}
                onChange={() => toggleCategory(cat)}
                className="h-5 w-5 accent-brand-600"
              />
            </label>
          ))}
        </div>
      </div>

      <div className="rounded-2xl bg-white p-5 ring-1 ring-slate-200">
        <h3 className="font-bold text-slate-800">Dominios personalizados</h3>
        <p className="text-sm text-slate-500">Uno por línea. Ej: facebook.com</p>
        <textarea
          value={domains}
          onChange={(e) => setDomains(e.target.value)}
          rows={4}
          placeholder={'facebook.com\ninstagram.com'}
          className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-brand-500"
        />
      </div>

      <button
        onClick={save}
        disabled={saving}
        className="rounded-xl bg-brand-600 px-6 py-3 font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
      >
        {saving ? 'Guardando…' : 'Guardar filtros'}
      </button>
    </div>
  )
}

function ActivityTab({ childUid, child }: { childUid: string; child: Child }) {
  const usage = child.windows?.usage ?? {}
  const entries = useMemo(() => Object.entries(usage).map(([name, seconds]) => ({ name, minutes: Math.round((seconds ?? 0) / 60) })).sort((a, b) => b.minutes - a.minutes).slice(0, 10), [usage])
  const totalMinutes = entries.reduce((acc, e) => acc + e.minutes, 0)
  const colors = ['#2569f2', '#60b0ff', '#93cdff', '#1f43b4', '#3b8bfd', '#60b0ff', '#93cdff', '#bfe0ff', '#1e52df', '#3b8bfd']
  const [filter, setFilter] = useState<'all'|'apps'|'web'|'youtube'|'calls'|'location'>('all')

  const timeline = useMemo(() => {
    const ev: { id:string; time:string; icon:string; title:string; desc:string; kind:string }[] = []
    const now = new Date().toISOString()
    // Apps
    Object.entries(usage).forEach(([name, sec]) => {
      const isYT = name.toLowerCase().includes('youtube') || name.toLowerCase().includes('yt')
      ev.push({ id:`app-${name}`, time: child.lastSeen ?? now, icon: isYT ? '▶️' : '📱', title: isYT ? `YouTube - ${name}` : name, desc: `${Math.round((sec??0)/60)} min hoy`, kind: isYT ? 'youtube' : 'apps' })
    })
    // Web historial (customDomains bloqueados + browsingHistory si existe)
    const browsing = (child as unknown as { browsingHistory?: {url:string; title:string; time:string}[] }).browsingHistory ?? []
    browsing.forEach((b, i) => ev.push({ id:`web-${i}`, time: b.time, icon:'🌐', title: b.title || b.url, desc: b.url, kind:'web' }))
    if (child.webFilter?.customDomains?.length) {
      child.webFilter.customDomains.forEach((d,i)=> ev.push({ id:`wblock-${i}`, time: now, icon:'🚫', title:`Dominio bloqueado: ${d}`, desc:`Filtro web`, kind:'web' }))
    }
    // YouTube historial explícito
    const ytHist = (child as unknown as { youtubeHistory?: {title:string; channel:string; time:string}[] }).youtubeHistory ?? []
    ytHist.forEach((y,i)=> ev.push({ id:`yt-${i}`, time: y.time, icon:'▶️', title: y.title, desc: y.channel, kind:'youtube' }))
    // Llamadas
    Object.values(child.calls ?? {}).forEach((c:any,i)=> ev.push({ id:`call-${i}`, time: c.callTime ?? now, icon: c.callType==='Outgoing Call'?'📤':'📥', title: c.contactName || c.phoneNumber, desc: `${c.callType} · ${c.callDurationInSeconds}s`, kind:'calls' }))
    // Ubicación
    if (child.location?.latitude) ev.push({ id:'loc', time: child.lastSeen ?? now, icon:'📍', title:`Ubicación ${child.location.latitude?.toFixed(4)}, ${child.location.longitude?.toFixed(4)}`, desc: child.location.outOfFence ? 'Fuera de zona' : 'En zona segura', kind:'location' })
    ev.sort((a,b)=> (b.time||'').localeCompare(a.time||''))
    return filter==='all' ? ev : ev.filter(e=>e.kind===filter)
  }, [usage, child, filter])

  return (
    <div className="space-y-5">
      <div className="grid gap-5 lg:grid-cols-2">
        <div className="rounded-2xl bg-white p-5 ring-1 ring-slate-200">
          <h3 className="font-bold text-slate-800">Tiempo de pantalla por app</h3>
          {entries.length === 0 ? (
            <p className="mt-4 text-sm text-slate-400">Sin datos de uso todavía. El agente irá reportando el uso por app.</p>
          ) : (
            <>
              <p className="mt-1 text-sm text-slate-500">{totalMinutes} min hoy · {entries.length} apps</p>
              <div className="mt-4 h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={entries} layout="vertical" margin={{ left: 8, right: 16 }}>
                    <XAxis type="number" allowDecimals={false} />
                    <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(v) => [`${v} min`, 'Tiempo']} />
                    <Bar dataKey="minutes" radius={[0, 6, 6, 0]}>{entries.map((_, i) => (<Cell key={i} fill={colors[i % colors.length]} />))}</Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
        </div>
        <ScreenTimeEditor childUid={childUid} child={child} />
      </div>

      <div className="rounded-2xl bg-white p-5 ring-1 ring-slate-200">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-bold text-slate-800">Cronología de actividad</h3>
          <span className="text-xs text-slate-400">Historial de navegación, vídeos de YouTube, tiempo de pantalla y más</span>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {(['all','apps','web','youtube','calls','location'] as const).map(k=>(
            <button key={k} onClick={()=>setFilter(k)} className={`rounded-full px-3 py-1 text-xs font-semibold ${filter===k ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>{k==='all'?'Todo':k==='apps'?'Apps':k==='web'?'Web':k==='youtube'?'YouTube':k==='calls'?'Llamadas':'Ubicación'}</button>
          ))}
        </div>
        <div className="mt-6 relative">
          <div className="absolute left-4 top-0 bottom-0 w-px bg-slate-200 hidden sm:block" />
          <div className="space-y-3">
            {timeline.length===0 ? <p className="py-8 text-center text-sm text-slate-400">Sin actividad registrada aún</p> : timeline.slice(0,50).map(e=>(
              <div key={e.id} className="relative flex gap-4 rounded-xl border border-slate-100 bg-slate-50 p-3 sm:pl-8">
                <span className="hidden sm:flex absolute left-2 top-3 h-6 w-6 items-center justify-center rounded-full bg-white text-xs ring-1 ring-slate-200">{e.icon}</span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-800">{e.title}</p>
                  <p className="truncate text-xs text-slate-500">{e.desc}</p>
                </div>
                <span className="shrink-0 text-xs text-slate-400">{e.time ? new Date(e.time).toLocaleString() : ''}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function ScreenTimeEditor({ childUid, child }: { childUid: string; child: Child }) {
  const [quota, setQuota] = useState<import('../lib/types').DailyQuota | null>(null)
  const [hours, setHours] = useState(2)
  const [minutes, setMinutes] = useState(0)
  const [enabled, setEnabled] = useState(true)
  const [saving, setSaving] = useState(false)
  useEffect(() => subscribeDailyQuota(childUid, (q) => {
    setQuota(q)
    if (q) { setEnabled(q.enabled); setHours(Math.floor(q.dailyMinutes/60)); setMinutes(q.dailyMinutes%60) }
    else { setEnabled(true); setHours(2); setMinutes(0) }
  }), [childUid])

  const save = async () => {
    setSaving(true)
    try {
      const dailyMinutes = hours*60 + minutes
      await updateDailyQuota(childUid, { enabled, dailyMinutes })
      toast('✅ Cuota diaria guardada', 'success')
    } finally { setSaving(false) }
  }
  const totalMin = Object.values(child.windows?.usage ?? {}).reduce((a,b)=>a+(b??0)/60,0)
  const remaining = quota && quota.enabled ? Math.max(0, quota.dailyMinutes - Math.round(totalMin)) : null

  return (
    <div className="rounded-2xl bg-white p-5 ring-1 ring-slate-200">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-slate-800 flex items-center">Cuota diaria <InfoTip text="Tiempo total permitido por día. Se pausa durante Rutinas. Ej: si Rutina Jue 09:00-14:35 está activa, la Cuota no descuenta. Si no hay rutina, al agotar los minutos se bloquea con 'Cuota agotada'." /></h3>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={enabled} onChange={e=>setEnabled(e.target.checked)} className="h-4 w-4 accent-brand-600" />
          <span className={enabled ? "text-emerald-600 font-medium" : "text-slate-400"}>{enabled ? "Activa" : "Desactivada"}</span>
        </label>
      </div>
      <p className="mt-1 text-sm text-slate-500">
        Tiempo total permitido por día. Se pausa durante rutinas. El botón superior Bloquear/Desbloquear es solo bloqueo manual.
      </p>
      {remaining !== null && (
        <p className="mt-2 text-sm">
          <span className={remaining===0 ? "text-rose-600 font-bold" : "text-emerald-600 font-bold"}>{remaining} min restantes hoy</span>
          <span className="text-slate-400"> · usado {Math.round(totalMin)} min</span>
        </p>
      )}
      <div className="mt-4 flex items-end gap-3">
        <div>
          <label className="text-xs font-medium text-slate-500">Horas</label>
          <input
            type="number"
            min={0}
            max={24}
            value={hours}
            onChange={(e) => setHours(Math.max(0, Number(e.target.value)))}
            disabled={!enabled}
            className="mt-1 w-24 rounded-xl border border-slate-200 px-3 py-2 text-lg font-bold text-slate-800 outline-none focus:border-brand-500 disabled:opacity-50"
          />
        </div>
        <span className="pb-3 text-lg font-bold text-slate-400">:</span>
        <div>
          <label className="text-xs font-medium text-slate-500">Minutos</label>
          <input
            type="number"
            min={0}
            max={59}
            value={minutes}
            onChange={(e) => setMinutes(Math.min(59, Math.max(0, Number(e.target.value))))}
            disabled={!enabled}
            className="mt-1 w-24 rounded-xl border border-slate-200 px-3 py-2 text-lg font-bold text-slate-800 outline-none focus:border-brand-500 disabled:opacity-50"
          />
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="ml-auto rounded-xl bg-brand-600 px-5 py-3 font-semibold text-white transition hover:bg-brand-700 disabled:opacity-60"
        >
          {saving ? 'Guardando…' : 'Guardar'}
        </button>
      </div>
      <p className="mt-4 text-sm text-slate-500">
        Cuota actual:{' '}
        <b className="text-slate-700">{quota ? `${Math.floor(quota.dailyMinutes/60)}h ${quota.dailyMinutes%60}m` : '2h 0m'}</b>
        {quota?.enabled && remaining===0 && (
          <span className="ml-2 rounded-full bg-rose-100 px-2.5 py-0.5 text-xs font-semibold text-rose-600">
            Cuota agotada
          </span>
        )}
      </p>
      <p className="mt-1 text-xs text-slate-400">El bloqueo manual (botón superior) es independiente de la cuota y de las rutinas.</p>
    </div>
  )
}

function AppsTab({ childUid, child }: { childUid: string; child: Child }) {
  const apps = child.apps ?? []
  return (
    <div className="overflow-hidden rounded-2xl bg-white ring-1 ring-slate-200">
      <div className="border-b border-slate-100 px-5 py-3 text-sm text-slate-500">
        {apps.length} aplicaciones instaladas · tiempo máximo diario{' '}
        <b className="text-slate-700">{formatScreenLock(child.screenLock)}</b>
      </div>
      <ul className="divide-y divide-slate-100">
        {apps.length === 0 && (
          <li className="px-5 py-8 text-center text-sm text-slate-400">
            Sin datos de aplicaciones todavía
          </li>
        )}
        {apps.map((app, i) => (
          <li key={app.packageName ?? i} className="flex items-center gap-3 px-5 py-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-lg">
              {appEmoji(app.packageName, app.appName)}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-slate-800">{app.appName}</p>
              <p className="truncate text-xs text-slate-400">{app.packageName}</p>
            </div>
            <label className="flex cursor-pointer items-center gap-2">
              <span className="text-xs font-medium text-slate-500">Bloqueada</span>
              <input
                type="checkbox"
                checked={Boolean(app.blocked)}
                onChange={(e) => {
                  setChildAppBlocked(childUid, app, e.target.checked)
                  toast(
                    e.target.checked
                      ? `🚫 ${app.appName} bloqueada`
                      : `✅ ${app.appName} habilitada`,
                    'success',
                  )
                }}
                className="h-5 w-5 accent-brand-600"
              />
            </label>
          </li>
        ))}
      </ul>
    </div>
  )
}

function CallsTab({ child }: { child: Child }) {
  const calls = useMemo(
    () => Object.values(child.calls ?? {}).sort((a, b) => (b.callTime ?? '').localeCompare(a.callTime ?? '')),
    [child.calls],
  )
  return (
    <div className="overflow-hidden rounded-2xl bg-white ring-1 ring-slate-200">
      <ListHeader text={`${calls.length} llamadas registradas`} />
      <ul className="divide-y divide-slate-100">
        {calls.length === 0 && <Empty text="Sin llamadas registradas" />}
        {calls.map((c, i) => (
          <li key={i} className="flex items-center gap-3 px-5 py-3">
            <span className="text-xl">{c.callType === 'Outgoing Call' ? '📤' : '📥'}</span>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-slate-800">{c.contactName || c.phoneNumber}</p>
              <p className="text-xs text-slate-400">
                {c.callTime} · {c.callDurationInSeconds}s
              </p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

function SmsTab({ child }: { child: Child }) {
  const messages = useMemo(
    () =>
      Object.values(child.messages ?? {}).sort((a, b) =>
        (b.timeReceived ?? '').localeCompare(a.timeReceived ?? ''),
      ),
    [child.messages],
  )
  return (
    <div className="overflow-hidden rounded-2xl bg-white ring-1 ring-slate-200">
      <ListHeader text={`${messages.length} mensajes`} />
      <ul className="divide-y divide-slate-100">
        {messages.length === 0 && <Empty text="Sin mensajes registrados" />}
        {messages.map((m, i) => (
          <li key={i} className="px-5 py-3">
            <div className="flex items-center justify-between">
              <p className="font-medium text-slate-800">
                {m.contactName || m.senderPhoneNumber}
              </p>
              <p className="text-xs text-slate-400">{m.timeReceived}</p>
            </div>
            <p className="mt-1 text-sm text-slate-600">{m.messageBody}</p>
          </li>
        ))}
      </ul>
    </div>
  )
}

function ContactsTab({ child }: { child: Child }) {
  const contacts = child.Contacts ?? []
  return (
    <div className="overflow-hidden rounded-2xl bg-white ring-1 ring-slate-200">
      <ListHeader text={`${contacts.length} contactos`} />
      <ul className="divide-y divide-slate-100">
        {contacts.length === 0 && <Empty text="Sin contactos registrados" />}
        {contacts.map((c, i) => (
          <li key={i} className="flex items-center gap-3 px-5 py-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 font-bold text-slate-600">
              {c.contactName?.charAt(0)?.toUpperCase() ?? '?'}
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-slate-800">{c.contactName}</p>
              <p className="text-xs text-slate-400">{c.contactNumber}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

function LocationTab({ child }: { child: Child }) {
  const loc = child.location
  const hasPos = typeof loc?.latitude === 'number' && typeof loc?.longitude === 'number'
  const center: [number, number] | null = hasPos ? [loc!.latitude as number, loc!.longitude as number] : null

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <div className="overflow-hidden rounded-2xl bg-white ring-1 ring-slate-200">
        <ListHeader text="Última ubicación conocida" />
        <div className="h-72 w-full">
          {center ? (
            <MapContainer center={center} zoom={14} className="h-full w-full">
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <Marker position={center} icon={customIcon}>
                <Popup>{child.name}</Popup>
              </Marker>
              {loc?.geoFence && typeof loc.fenceCenterLatitude === 'number' && (
                <Circle
                  center={[loc.fenceCenterLatitude, loc.fenceCenterLongitude ?? 0]}
                  radius={loc.fenceDiameter ?? 500}
                  pathOptions={{ color: '#2569f2', fillColor: '#93cdff', fillOpacity: 0.2 }}
                />
              )}
            </MapContainer>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-slate-400">
              Sin ubicación disponible
            </div>
          )}
        </div>
      </div>

      <div className="space-y-3">
        <div className="rounded-2xl bg-white p-5 ring-1 ring-slate-200">
          <p className="text-sm font-semibold text-slate-700">Coordenadas</p>
          {hasPos ? (
            <p className="mt-1 text-2xl font-bold text-brand-600">
              {loc!.latitude!.toFixed(5)}, {loc!.longitude!.toFixed(5)}
            </p>
          ) : (
            <p className="mt-1 text-slate-400">—</p>
          )}
        </div>
        <div className="rounded-2xl bg-white p-5 ring-1 ring-slate-200">
          <p className="text-sm font-semibold text-slate-700">Geo-cerca</p>
          <p className="mt-1 text-slate-600">
            {loc?.geoFence ? 'Activada' : 'Desactivada'}
            {loc?.geoFence && typeof loc.fenceDiameter === 'number' && (
              <span className="ml-2 text-slate-400">· radio {Math.round(loc.fenceDiameter)} m</span>
            )}
          </p>
        </div>
        <div
          className={`rounded-2xl p-5 ring-1 ${
            loc?.outOfFence
              ? 'bg-rose-50 ring-rose-200'
              : 'bg-emerald-50 ring-emerald-200'
          }`}
        >
          <p className={`text-sm font-semibold ${loc?.outOfFence ? 'text-rose-600' : 'text-emerald-600'}`}>
            {loc?.outOfFence ? '⚠️ El niño está FUERA de la zona segura' : '✅ El niño está dentro de la zona segura'}
          </p>
        </div>
      </div>
    </div>
  )
}

function InfoTip({ text }: { text: string }) {
  return (
    <span className="group relative ml-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-amber-100 text-xs font-bold text-amber-700 cursor-help">?
      <span className="pointer-events-none absolute left-6 top-0 z-10 hidden w-64 rounded-xl bg-slate-800 p-3 text-xs leading-relaxed text-white shadow-xl group-hover:block">
        {text}
      </span>
    </span>
  )
}

function RoutinesTab({ childUid }: { childUid: string }) {
  const defaultRoutines = Object.fromEntries(WEEK_DAYS.map(d => [d.id, { enabled: false, from: '21:00', to: '07:00' }])) as Routines
  const [routines, setRoutines] = useState<Routines | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [saving, setSaving] = useState(false)
  useEffect(() => subscribeRoutines(childUid, (r) => { setRoutines(r ?? defaultRoutines); setLoaded(true) }), [childUid])
  const setDay = (day: WeekDay, patch: Partial<{ enabled: boolean; from: string; to: string }>) => {
    setRoutines((prev) => {
      const base: Routines = prev ?? Object.fromEntries(WEEK_DAYS.map(d => [d.id, { enabled: false, from: '21:00', to: '07:00' }])) as Routines
      return { ...base, [day]: { ...base[day], ...patch } }
    })
  }
  const save = async () => {
    if (!routines) return
    setSaving(true)
    try { await updateRoutines(childUid, routines); toast('✅ Rutinas guardadas', 'success') } finally { setSaving(false) }
  }
  if (!loaded) return <div className="rounded-2xl bg-white p-6 text-center text-slate-400 ring-1 ring-slate-200">Cargando rutinas…</div>
  if (!routines) return <div className="rounded-2xl bg-white p-6 text-center text-rose-500 ring-1 ring-slate-200">Error cargando rutinas</div>
  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-white p-5 ring-1 ring-slate-200">
        <h3 className="font-bold text-slate-800 flex items-center">Rutinas - Ayuda a equilibrar el tiempo en Internet <InfoTip text="Bloqueo por horario. Ej: Jue 09:00-14:35. Cuando una rutina está activa, la Cuota diaria se pausa automáticamente y no descuenta. Si no hay rutina activa, la Cuota sí bloquea al agotar el tiempo." /></h3>
        <p className="mt-1 text-sm text-slate-500">Define por día el intervalo bloqueado. Ej: Lunes 21:00 → 07:00 (hora de dormir). Configura Días y Horas De-Hasta.</p>
      </div>
      <div className="overflow-hidden rounded-2xl bg-white ring-1 ring-slate-200">
        <div className="divide-y divide-slate-100">
          {WEEK_DAYS.map(({ id, label }) => {
            const r = routines[id] ?? { enabled: false, from: '21:00', to: '07:00' }
            return (
              <div key={id} className="flex flex-wrap items-center gap-3 px-5 py-3">
                <label className="flex items-center gap-2 w-32">
                  <input type="checkbox" checked={r.enabled} onChange={e => setDay(id, { enabled: e.target.checked })} className="h-4 w-4 accent-brand-600" />
                  <span className="text-sm font-medium text-slate-700">{label}</span>
                </label>
                <span className="text-sm text-slate-500">De</span>
                <input type="time" value={r.from} onChange={e => setDay(id, { from: e.target.value })} disabled={!r.enabled} className="rounded-lg border border-slate-200 px-2 py-1 text-sm disabled:opacity-50" />
                <span className="text-sm text-slate-500">Hasta</span>
                <input type="time" value={r.to} onChange={e => setDay(id, { to: e.target.value })} disabled={!r.enabled} className="rounded-lg border border-slate-200 px-2 py-1 text-sm disabled:opacity-50" />
              </div>
            )
          })}
        </div>
      </div>
      <button onClick={save} disabled={saving} className="rounded-xl bg-brand-600 px-6 py-3 font-semibold text-white hover:bg-brand-700 disabled:opacity-60">{saving ? 'Guardando…' : 'Guardar rutinas'}</button>
    </div>
  )
}

function UninstallSection({ childUid, childName }: { childUid: string; childName: string }) {
  const [req, setReq] = useState<import('../lib/types').UninstallRequest | null>(null)
  const [busy, setBusy] = useState(false)
  useEffect(() => subscribeUninstallRequest(childUid, setReq), [childUid])
  const gen = async () => {
    setBusy(true)
    try {
      const code = await requestUninstallCode(childUid, auth.currentUser?.uid ?? '')
      toast(`🔑 Código ${code} válido 10 min para ${childName}`, 'success')
    } catch (e: unknown) { toast((e as Error).message, 'error') } finally { setBusy(false) }
  }
  const expires = req ? new Date(req.expiresAt).toLocaleTimeString() : ''
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
      <h4 className="font-bold text-amber-800">Desinstalar agente</h4>
      <p className="mt-1 text-sm text-amber-700">Genera una clave de 6 dígitos (10 min). En el PC/móvil del niño: bandeja KidSafe → Desinstalar → ingresa la clave. También puedes auto-desinstalar sin clave si el agente ve la solicitud.</p>
      {req && <p className="mt-2 font-mono text-lg font-bold text-amber-900">Clave actual: {req.code} (expira {expires})</p>}
      <button onClick={gen} disabled={busy} className="mt-3 rounded-xl bg-amber-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-60">{busy ? 'Generando…' : req ? 'Regenerar clave' : 'Generar clave desinstalación'}</button>
    </div>
  )
}

function ListHeader({ text }: { text: string }) {
  return (
    <div className="border-b border-slate-100 px-5 py-3 text-sm text-slate-500">{text}</div>
  )
}

function Empty({ text }: { text: string }) {
  return <li className="px-5 py-8 text-center text-sm text-slate-400">{text}</li>
}
